import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body4Skeleton } from '../../Skeletons/Skeletons';
import { HiCheck } from 'react-icons/hi';
import { FaCalendarAlt, FaClock, FaStar, FaCalendarCheck } from 'react-icons/fa';
import DownloadAppModal from '../../Modals/DownloadAppModal';

const iconBgs = ['bg-orange-100', 'bg-green-100', 'bg-amber-100', 'bg-blue-100', 'bg-purple-100', 'bg-red-100'];
const iconColors = ['text-orange-500', 'text-green-500', 'text-amber-500', 'text-blue-500', 'text-purple-500', 'text-red-400'];

const Body4 = () => {
  const { data: homeData, isOutsideProvider } = useHomeData();
  const [localData, setLocalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fallback fetch for when used outside HomeDataProvider
  useEffect(() => {
    if (!isOutsideProvider) return;
    const fetchBody4Data = async () => {
      try {
        const [mainHeadingSnap, subHeadingSnap] = await Promise.all([
          getDoc(doc(db, 'home', 'body4', 'mainHeading', 'mainHeading')),
          getDoc(doc(db, 'home', 'body4', 'mainHeading', 'subHeading')),
        ]);
        const cardPromises = [];
        for (let i = 1; i <= 6; i++) {
          cardPromises.push(
            getDoc(doc(db, 'home', 'body4', 'mainHeading', 'mainCards', `mainCard${i}`, `mainCard${i}`))
          );
        }
        const cardSnaps = await Promise.all(cardPromises);
        const features = [];
        cardSnaps.forEach((snap, i) => {
          if (snap.exists()) {
            const data = snap.data();
            features.push({
              title: data.heading || '',
              description: data.content || '',
              img: data.img || null,
              iconBg: iconBgs[i],
              iconColor: iconColors[i],
            });
          }
        });
        setLocalData({
          mainHeading: mainHeadingSnap.exists() ? (mainHeadingSnap.data().text || '') : '',
          subHeading: subHeadingSnap.exists() ? (subHeadingSnap.data().text || '') : '',
          features,
        });
      } catch (error) {
        console.error('Error fetching Body4 data:', error);
      }
    };
    fetchBody4Data();
  }, [isOutsideProvider]);

  const body4Data = isOutsideProvider ? localData : homeData?.body4;

  if (!body4Data || body4Data.features.length === 0) {
    return <Body4Skeleton />;
  }

  return (
    <section className="py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-white">
      {/* Heading */}
      <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-14">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {body4Data.mainHeading}
        </h2>
        <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
          {body4Data.subHeading}
        </p>
      </div>

      {/* Feature Cards - Compact Height, Expanded Width */}
      <div className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-x-6 lg:gap-x-20 xl:gap-x-24 lg:gap-y-8">
          {body4Data.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-center gap-4 lg:gap-6 p-4 sm:p-5 lg:px-8 bg-white rounded-2xl border-2 border-gray-200 shadow-[6px_6px_0px_0px_#e5e7eb] lg:hover:shadow-[0px_0px_0px_0px_#e5e7eb] lg:hover:translate-x-[6px] lg:hover:translate-y-[6px] transition-all duration-200 group lg:cursor-pointer"
            >
              {/* Icon/Image */}
              <div className={`
                w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0
                ${feature.iconBg} bg-opacity-40 lg:group-hover:bg-opacity-100 transition-all duration-300
              `}>
                {feature.img ? (
                  <img
                    src={feature.img}
                    alt={feature.title}
                    width={40}
                    height={40}
                    loading="lazy"
                    decoding="async"
                    className="w-7 h-7 sm:w-8 sm:h-8 object-contain lg:group-hover:scale-110 transition-transform duration-300"
                  />
                ) : (
                  <HiCheck className={`text-xl sm:text-2xl ${feature.iconColor}`} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-0.5 sm:mb-1 lg:group-hover:text-orange-600 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 leading-snug">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center mt-12 sm:mt-16">
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-200 hover:shadow-lg hover:shadow-orange-200/50"
        >
          Start your subscription
        </button>
        <p className="text-gray-500 text-sm sm:text-base mt-4">
          Cancel anytime. No long-term commitment.
        </p>
      </div>

      <DownloadAppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default Body4;
